import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Mail, AlertCircle, CheckCircle, Loader2, Trash2, ServerCog, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { companyAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import axios from 'axios';

const API_BASE_LOCAL = process.env.REACT_APP_BACKEND_URL;

export default function ZimbraSettingsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [config, setConfig] = useState({
        server_url: '',
        admin_username: '',
        admin_token: '',
        default_domain: '',
        smtp_port: 587,
        imap_port: 993,
        use_ssl: true
    });
    const [isConfigured, setIsConfigured] = useState(false);

    // Connection-test state
    const [testEmail, setTestEmail] = useState('');
    const [testPassword, setTestPassword] = useState('');
    const [imapResult, setImapResult] = useState(null); // null | {success, stage, error?, ...}
    const [smtpResult, setSmtpResult] = useState(null);
    const [imapTesting, setImapTesting] = useState(false);
    const [smtpTesting, setSmtpTesting] = useState(false);

    const runConnTest = async (kind) => {
        if (!companyId) { toast.error('Company not loaded'); return; }
        if (!config.server_url) { toast.error('Isi Server URL terlebih dahulu'); return; }
        const setLoading = kind === 'imap' ? setImapTesting : setSmtpTesting;
        const setResult = kind === 'imap' ? setImapResult : setSmtpResult;
        setLoading(true);
        setResult(null);
        try {
            const payload = {
                server_url: config.server_url,
                use_ssl: config.use_ssl,
                ...(kind === 'imap' ? { imap_port: config.imap_port } : { smtp_port: config.smtp_port }),
                ...(testEmail && testPassword ? { test_email: testEmail, test_password: testPassword } : {}),
            };
            const res = await axios.post(
                `${API_BASE_LOCAL}/api/companies/${companyId}/zimbra-config/test-${kind}`,
                payload,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
            );
            setResult(res.data);
            if (res.data?.success) {
                toast.success(
                    kind.toUpperCase() + ' OK' +
                    (res.data.authenticated ? ' (auth verified)' : ' (connectivity only)')
                );
            } else {
                toast.error(`${kind.toUpperCase()} gagal di stage "${res.data?.stage}"`);
            }
        } catch (err) {
            const detail = err.response?.data?.detail || err.message;
            setResult({ success: false, stage: 'request', error: detail });
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    // Fetch company_id from API
    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                const authToken = localStorage.getItem('token');
                if (!authToken) return;
                
                const res = await fetch(`${API_BASE}/api/companies/my`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.id) {
                        setCompanyId(data.id);
                    } else {
                        toast.error('You are not associated with any company');
                        setLoading(false);
                    }
                } else {
                    toast.error('Failed to fetch company information');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Failed to fetch company:', error);
                toast.error('Failed to fetch company information');
                setLoading(false);
            }
        };
        
        fetchCompany();
    }, []);

    // Load config when companyId is available
    useEffect(() => {
        if (companyId) {
            loadConfig();
        }
    }, [companyId]);

    const loadConfig = async () => {
        try {
            setLoading(true);
            if (!companyId) {
                return;
            }

            const response = await companyAPI.getZimbraConfig(companyId);
            
            if (response.data && response.data.configured) {
                setConfig({
                    server_url: response.data.server_url || '',
                    admin_username: response.data.admin_username || '',
                    admin_token: '', // Don't show token
                    default_domain: response.data.default_domain || '',
                    smtp_port: response.data.smtp_port || 587,
                    imap_port: response.data.imap_port || 993,
                    use_ssl: response.data.use_ssl !== false
                });
                setIsConfigured(true);
            }
        } catch (error) {
            console.error('Failed to load Zimbra config:', error);
            // Config might not exist yet, that's okay
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Validation
        if (!config.server_url || !config.admin_username || !config.admin_token || !config.default_domain) {
            toast.error('Please fill all required fields');
            return;
        }

        if (!companyId) {
            toast.error('No company selected');
            return;
        }

        try {
            setSaving(true);

            await companyAPI.saveZimbraConfig(companyId, config);

            toast.success('Zimbra configuration saved successfully!');
            setIsConfigured(true);
        } catch (error) {
            console.error('Failed to save Zimbra config:', error);
            toast.error('Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete Zimbra configuration?')) {
            return;
        }

        if (!companyId) {
            toast.error('No company selected');
            return;
        }

        try {
            setSaving(true);

            await companyAPI.deleteZimbraConfig(companyId);

            toast.success('Zimbra configuration deleted');
            setConfig({
                server_url: '',
                admin_username: '',
                admin_token: '',
                default_domain: '',
                smtp_port: 587,
                imap_port: 993,
                use_ssl: true
            });
            setIsConfigured(false);
        } catch (error) {
            console.error('Failed to delete Zimbra config:', error);
            toast.error('Failed to delete configuration');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Mail className="w-8 h-8 text-brand-primary" />
                        <div>
                            <h1 className="text-2xl font-bold text-brand-text-primary">Zimbra Mail Settings</h1>
                            <p className="text-sm text-brand-text-muted">
                                Configure Zimbra Opensource v10 integration for company email
                            </p>
                        </div>
                    </div>
                    {isConfigured && (
                        <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">Configured</span>
                        </div>
                    )}
                </div>

                {/* Instructions */}
                <Card className="border-brand-border bg-blue-50">
                    <CardContent className="p-4">
                        <div className="flex gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-gray-700 space-y-2">
                                <p className="font-semibold">How it works:</p>
                                <ul className="list-disc list-inside space-y-1 ml-2">
                                    <li>Configure Zimbra server credentials below</li>
                                    <li>Users will automatically get email accounts when they access Mail menu</li>
                                    <li>Email format: username@{config.default_domain || 'yourdomain.com'}</li>
                                    <li>Users can read and send emails using the Mail feature</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Configuration Form */}
                <Card className="border-brand-border">
                    <CardHeader>
                        <CardTitle>Server Configuration</CardTitle>
                        <CardDescription>Enter your Zimbra server details</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Server URL */}
                        <div>
                            <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                Server URL <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="url"
                                value={config.server_url}
                                onChange={(e) => setConfig({ ...config, server_url: e.target.value })}
                                placeholder="https://mail.yourdomain.com"
                                className="w-full px-4 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            />
                        </div>

                        {/* Admin Username */}
                        <div>
                            <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                Admin Username <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={config.admin_username}
                                onChange={(e) => setConfig({ ...config, admin_username: e.target.value })}
                                placeholder="zimbra"
                                className="w-full px-4 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            />
                        </div>

                        {/* Admin Token */}
                        <div>
                            <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                Admin Auth Token <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={config.admin_token}
                                onChange={(e) => setConfig({ ...config, admin_token: e.target.value })}
                                placeholder="Enter Zimbra admin authentication token"
                                rows={3}
                                className="w-full px-4 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent font-mono text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Get this from Zimbra admin console (Admin → Auth Token)
                            </p>
                        </div>

                        {/* Default Domain */}
                        <div>
                            <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                Default Email Domain <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={config.default_domain}
                                onChange={(e) => setConfig({ ...config, default_domain: e.target.value })}
                                placeholder="dayagagas.com"
                                className="w-full px-4 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Email addresses will be created as: username@{config.default_domain || 'domain.com'}
                            </p>
                        </div>

                        {/* Ports */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                    SMTP Port
                                </label>
                                <input
                                    type="number"
                                    value={config.smtp_port}
                                    onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                                    className="w-full px-4 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                    IMAP Port
                                </label>
                                <input
                                    type="number"
                                    value={config.imap_port}
                                    onChange={(e) => setConfig({ ...config, imap_port: parseInt(e.target.value) })}
                                    className="w-full px-4 py-2 border border-brand-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Use SSL */}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="use_ssl"
                                checked={config.use_ssl}
                                onChange={(e) => setConfig({ ...config, use_ssl: e.target.checked })}
                                className="w-4 h-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary"
                            />
                            <label htmlFor="use_ssl" className="text-sm font-medium text-brand-text-primary">
                                Use SSL/TLS (Recommended)
                            </label>
                        </div>

                        {/* Connection Test panel */}
                        <div className="border-t border-brand-border pt-4 mt-2">
                            <div className="flex items-center gap-2 mb-3">
                                <ServerCog className="w-5 h-5 text-brand-primary" />
                                <h3 className="font-semibold text-brand-text-primary">Cek Koneksi IMAP & SMTP</h3>
                            </div>
                            <p className="text-xs text-brand-text-muted mb-3">
                                Verifikasi server bisa dijangkau. Jika Anda mengisi email + password user di bawah,
                                cek juga akan mencoba login (auth verified). Tanpa kredensial → cek konektivitas saja.
                            </p>

                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <input type="email" value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    placeholder="Email user (opsional)"
                                    className="px-3 py-2 border border-brand-border rounded-lg text-sm focus:ring-2 focus:ring-brand-primary"
                                    data-testid="zimbra-test-email" />
                                <input type="password" value={testPassword}
                                    onChange={(e) => setTestPassword(e.target.value)}
                                    placeholder="Password user (opsional)"
                                    className="px-3 py-2 border border-brand-border rounded-lg text-sm focus:ring-2 focus:ring-brand-primary"
                                    data-testid="zimbra-test-password" />
                            </div>

                            <div className="flex gap-3">
                                <Button type="button" variant="outline" disabled={imapTesting}
                                    onClick={() => runConnTest('imap')}
                                    className="flex items-center gap-2"
                                    data-testid="test-imap-btn">
                                    {imapTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ServerCog className="w-4 h-4" />}
                                    Test IMAP
                                </Button>
                                <Button type="button" variant="outline" disabled={smtpTesting}
                                    onClick={() => runConnTest('smtp')}
                                    className="flex items-center gap-2"
                                    data-testid="test-smtp-btn">
                                    {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ServerCog className="w-4 h-4" />}
                                    Test SMTP
                                </Button>
                            </div>

                            {imapResult && (
                                <ConnTestResult kind="IMAP" result={imapResult} />
                            )}
                            {smtpResult && (
                                <ConnTestResult kind="SMTP" result={smtpResult} />
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-4">
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                Save Configuration
                            </Button>

                            {isConfigured && (
                                <Button
                                    variant="destructive"
                                    onClick={handleDelete}
                                    disabled={saving}
                                >
                                    Delete Configuration
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}

function ConnTestResult({ kind, result }) {
    const ok = result?.success;
    const Icon = ok ? CheckCircle : XCircle;
    return (
        <div className={`mt-3 p-3 rounded border text-sm ${
            ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}
            data-testid={`zimbra-${kind.toLowerCase()}-result`}>
            <div className="flex items-center gap-2 font-medium">
                <Icon className="w-4 h-4" />
                {kind} {ok ? 'OK' : 'Failed'} — stage: <span className="font-mono">{result.stage}</span>
                {ok && (
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
                        result.authenticated ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                        {result.authenticated ? 'auth verified' : 'connectivity only'}
                    </span>
                )}
            </div>
            {result.error && (
                <p className="mt-2 text-xs font-mono break-all">{result.error}</p>
            )}
            {ok && result.capabilities && (
                <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-green-900">Capabilities</summary>
                    <p className="mt-1 text-xs font-mono break-all">{result.capabilities}</p>
                </details>
            )}
            {ok && result.features && result.features.length > 0 && (
                <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-green-900">Features</summary>
                    <p className="mt-1 text-xs font-mono break-all">{result.features.join(', ')}</p>
                </details>
            )}
        </div>
    );
}
